export type HealthStatus =
  'Healthy' | 'Degraded' | 'Unhealthy' | 'NoData' | 'Error' | 'AccessDenied';

export type RequestOutcome = 'Success' | 'ClientError' | 'ServerError';

export const STATUS_LABELS: Record<HealthStatus, string> = {
  Healthy: 'Healthy',
  Degraded: 'Degraded',
  Unhealthy: 'Unhealthy',
  NoData: 'No data',
  Error: 'Error',
  AccessDenied: 'No access',
};

export type CallDirection = 'Incoming' | 'Outgoing';

export interface RequestLogEntry {
  timestamp: string;
  direction: CallDirection;
  channel: string;
  name: string;
  resultCode: string;
  success: boolean;
  outcome: RequestOutcome;
  durationMs: number;
}

export interface ExceptionSummary {
  type: string;
  count: number;
  lastSeen: string;
}

export interface HealthRules {
  healthyThreshold: number;
  degradedThreshold: number;
  requestCount: number;
  exceptionWindowMinutes: number;
  exceptionDegradedCount: number;
  exceptionUnhealthyCount: number;
  refreshSeconds: number;
  callTypes: string[];
}

export interface HealthChannel {
  name: string;
  total: number;
  succeeded: number;
  clientErrors: number;
  failed: number;
  percentage: number;
  status: HealthStatus;
}

export interface AppHealth {
  appId: string;
  name: string | null;
  roleName: string | null;
  status: HealthStatus;
  statusReason: string | null;
  totalRequests: number;
  successfulRequests: number;
  clientErrorRequests: number;
  failedRequests: number;
  healthPercentage: number | null;
  channels: HealthChannel[];
  latestRequests: RequestLogEntry[];
  exceptionCount: number;
  exceptionWindowMinutes: number;
  exceptions: ExceptionSummary[];
  error: string | null;
  checkedAt: string;
  resourceGroup: string | null;
  subscriptionId: string | null;
}

export type DeadLetterStatus = 'Clean' | 'DeadLettered' | 'NotFound' | 'AccessDenied' | 'Error';

export type ServiceBusEntityKind = 'Queue' | 'Topic';

export const DEAD_LETTER_STATUS_LABELS: Record<DeadLetterStatus, string> = {
  Clean: 'No dead letters',
  DeadLettered: 'Dead letters',
  NotFound: 'Not found',
  AccessDenied: 'No access',
  Error: 'Error',
};

// Dead letters of a topic sit on its subscriptions.
export interface ServiceBusSubscription {
  name: string;
  entityStatus: string | null;
  activeMessageCount: number;
  deadLetterMessageCount: number;
  accessedAt: string | null;
}

// For a topic the counts are summed over its subscriptions.
export interface ServiceBusEntity {
  namespace: string;
  name: string;
  kind: ServiceBusEntityKind;
  subscriptionId: string;
  resourceGroup: string;
  status: DeadLetterStatus;
  error: string | null;
  deadLetterMessageCount: number;
  activeMessageCount: number;
  scheduledMessageCount: number;
  sizeInBytes: number;
  entityStatus: string | null;
  accessedAt: string | null;
  updatedAt: string | null;
  subscriptions: ServiceBusSubscription[];
  checkedAt: string;
}

export interface ServiceBusReport {
  refreshSeconds: number;
  entities: ServiceBusEntity[];
}
